/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * One action — the same detail shape as the other notification-engine masters:
 * a hero stating what it is, with its actions, then cards for what it does.
 *
 * Activating and deactivating live here, beside Edit. The list only shows the
 * status; a status change is made where the thing is read.
 */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { actionMastersService } from '../../services/action-masters.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import ActiveTag from '../../components/common/active-tag';
import { isRowActive } from '../../utils/active-state';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, WarnBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';

const JSON_PRE = 'max-h-72 overflow-auto rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-900/30 dark:text-white whitespace-pre-wrap break-words font-mono';

const ActionMasterView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'action', basePath: '/action-masters' });
  const canUpdate = useCanUpdate(JOB_NAMES.ACTION_MASTER);
  const toast = useRef<Toast>(null);
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['actionMaster', id],
    queryFn: () => actionMastersService.getById(id!),
    enabled: isReady && !!id,
  });

  // One mutation for both directions: the button shows whichever applies.
  const statusM = useMutation({
    mutationFn: (active: boolean) => (active
      ? actionMastersService.activate(id!)
      : actionMastersService.deactivate(id!)),
    onSuccess: (_d, active) => {
      qc.invalidateQueries({ queryKey: ['actionMaster', id] });
      qc.invalidateQueries({ queryKey: ['actionMasters'] });
      toast.current?.show({
        severity: 'success',
        summary: active ? 'Activated' : 'Deactivated',
        detail: active
          ? 'It is offered to recipients again from the next notification.'
          : 'It is no longer offered. Responses already recorded are unchanged.',
        life: 4000,
      });
    },
    onError: (e: any, active) => toast.current?.show({
      severity: 'error',
      summary: active ? 'Could not activate' : 'Could not deactivate',
      detail: extractErrorMessage(e, active ? 'Activate failed' : 'Deactivate failed'),
      life: 10000,
    }),
  });

  if (isLoading) return <PageSkeleton />;
  if (isError || !data) return (
    <div className="p-4">
      <Message severity="error" text={extractErrorMessage(error, 'Action not found')} className="mb-4 w-full" />
      <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigateToList()} />
    </div>
  );

  const active = isRowActive(data);
  const editable = canUpdate && !data.isDelete;
  const configuration = ((data as any).configuration || {}) as Record<string, any>;
  const followUpCode = configuration.followUp?.notificationCode as string | undefined;

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-ActionMaster-View">
      <Toast ref={toast} position="top-right" />
      <BackLink label="Actions" onClick={() => navigateToList()} />

      <Hero
        icon="zap"
        chip="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400"
        code={data.actionCode}
        tags={<>
          <Tag value={data.actionType} severity="secondary" className="text-xs px-1.5 py-0.5" />
          <ActiveTag row={data} />
        </>}
        description={data.description}
        actions={editable ? <>
          {active ? (
            <Button label="Deactivate" icon="pi pi-ban" size="small" severity="secondary" outlined
              onClick={() => statusM.mutate(false)} disabled={statusM.isPending}
              data-testid="ActionMasterView.Deactivate" />
          ) : (
            <Button label="Activate" icon="pi pi-check-circle" size="small" severity="success" outlined
              onClick={() => statusM.mutate(true)} disabled={statusM.isPending}
              data-testid="ActionMasterView.Activate" />
          )}
          <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary"
            onClick={() => id && navigateToEdit(id)} />
        </> : undefined}
        banner={active
          ? <ActiveBanner text="Active — offered to recipients wherever it is attached. Edits apply from the next notification." />
          : <WarnBanner text="Inactive — not offered to anyone. Responses already recorded are unchanged; activate it to offer it again." />}
      />

      <div className={CARD}>
        <CardHeader icon="sliders" color="text-amber-500" title="What it does" />
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-2">
          <Field label="Effect" mono>{data.effectKey || 'None — records the response only'}</Field>
          <Field label="Follow-up notification" mono>{followUpCode || 'None'}</Field>
          <Field label="Permission">{data.permissionKey || 'Not gated — every recipient offered it may take it'}</Field>
          <Field label="Memo">{data.isMemoRequired ? 'Required — refused without a note' : 'Optional'}</Field>
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="code" color="text-sky-400" title="Configuration" />
        <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Configuration</div>
            <pre className={JSON_PRE}>{JSON.stringify(configuration, null, 2)}</pre>
          </div>
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Style</div>
            <pre className={JSON_PRE}>{JSON.stringify(data.style || {}, null, 2)}</pre>
          </div>
        </div>
      </div>

      <RecordMeta data={data} extra={<Field label="Action code" mono>{data.actionCode}</Field>} />
    </div>
  );
};

export default ActionMasterView;
